import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

// Styled components matching the tickets table design
const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  "&::-webkit-scrollbar": {
    height: "8px",
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.5),
    },
  },
}));

function Allowances() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [creditors, setCreditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCreditor, setSelectedCreditor] = useState(null);
  
  const creditorsPerPage = 10;

  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";

  // Sample data - replace with API call
  useEffect(() => {
    fetchCreditors();
  }, []);

  const fetchCreditors = async () => {
    try {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        const sampleData = [
          {
            id: 1,
            name: "Tax Authority",
            calculationMethod: "Percentage",
            basePayroll: "Gross Salary",
            rate: "5%",
            bankAccountNumber: "1234567890",
          },
          {
            id: 2,
            name: "Pension Fund",
            calculationMethod: "Fixed Amount",
            basePayroll: "Basic Salary",
            rate: "1000",
            bankAccountNumber: "0987654321",
          },
          {
            id: 3,
            name: "Health Insurance",
            calculationMethod: "Percentage",
            basePayroll: "Gross Salary",
            rate: "3%",
            bankAccountNumber: "1122334455",
          },
          {
            id: 4,
            name: "Union Dues",
            calculationMethod: "Fixed Amount",
            basePayroll: "Basic Salary",
            rate: "500",
            bankAccountNumber: "5544332211",
          },
          {
            id: 5,
            name: "Loan Repayment",
            calculationMethod: "Fixed Amount",
            basePayroll: "Net Salary",
            rate: "2500",
            bankAccountNumber: "6677889900",
          },
        ];
        setCreditors(sampleData);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Error fetching creditors:", error);
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "desc" ? "asc" : "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedCreditors = [...creditors].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
    }
    return 0;
  });

  const indexOfLastCreditor = currentPage * creditorsPerPage;
  const indexOfFirstCreditor = indexOfLastCreditor - creditorsPerPage;
  const currentCreditors = sortedCreditors.slice(
    indexOfFirstCreditor,
    indexOfLastCreditor
  );

  const handleMenuOpen = (event, creditor) => {
    setAnchorEl(event.currentTarget);
    setSelectedCreditor(creditor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCreditor(null);
  };

  const handleAddNew = () => {
    console.log("Add new creditor");
    // Add your logic here
  };

  const handleEdit = () => {
    console.log("Edit creditor:", selectedCreditor);
    // Add your logic here
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log("Delete creditor:", selectedCreditor);
    // Add your logic here
    handleMenuClose();
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: 3,
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Creditors
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {creditors.length} {creditors.length === 1 ? "creditor" : "creditors"} found
        </Typography>
      </Box>

      <ResponsiveTableContainer component={Paper}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.light,
                "& th": {
                  color: "white",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                },
              }}
            >
              {[
                { id: "id", label: "ID" },
                { id: "name", label: "Name" },
                { id: "calculationMethod", label: "Calculation Method" },
                { id: "basePayroll", label: "Base Payroll" },
                { id: "rate", label: "Rate" },
                { id: "bankAccountNumber", label: "Bank Account Number" },
                { id: "actions", label: "Actions" },
              ].map((column) => (
                <ModernTableCell
                  key={column.id}
                  onClick={() => {
                    if (column.id !== "actions") {
                      handleSort(column.id);
                    }
                  }}
                  sx={{
                    cursor: column.id !== "actions" ? "pointer" : "default",
                    "&:hover": {
                      backgroundColor:
                        column.id !== "actions"
                          ? alpha("#fff", 0.1)
                          : "inherit",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    {column.label}
                    {sortConfig.key === column.id && (
                      <Box ml={1} display="flex" flexDirection="column">
                        <span
                          style={{
                            lineHeight: 0.5,
                            color:
                              sortConfig.direction === "asc"
                                ? "#fff"
                                : alpha("#fff", 0.5),
                            fontSize: "0.7rem",
                          }}
                        >
                          ▲
                        </span>
                        <span
                          style={{
                            lineHeight: 0.5,
                            color:
                              sortConfig.direction === "desc"
                                ? "#fff"
                                : alpha("#fff", 0.5),
                            fontSize: "0.7rem",
                          }}
                        >
                          ▼
                        </span>
                      </Box>
                    )}
                  </Box>
                </ModernTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading Allowances...
                  </Typography>
                </ModernTableCell>
              </TableRow>
            ) : currentCreditors.length > 0 ? (
              currentCreditors.map((creditor) => (
                <StyledTableRow key={creditor.id} hover>
                  <ModernTableCell>{creditor.id}</ModernTableCell>
                  <ModernTableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {creditor.name}
                    </Typography>
                  </ModernTableCell>
                  <ModernTableCell>{creditor.calculationMethod}</ModernTableCell>
                  <ModernTableCell>{creditor.basePayroll}</ModernTableCell>
                  <ModernTableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: PRIMARY_COLOR,
                      }}
                    >
                      {creditor.rate}
                    </Typography>
                  </ModernTableCell>
                  <ModernTableCell>{creditor.bankAccountNumber}</ModernTableCell>
                  <ModernTableCell>
                    <Tooltip title="More actions" arrow>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, creditor)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          "&:hover": {
                            backgroundColor: alpha(PRIMARY_COLOR, 0.1),
                          },
                        }}
                      >
                        <MoreVertIcon fontSize={isMobile ? "small" : "medium"} />
                      </IconButton>
                    </Tooltip>
                  </ModernTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <ModernTableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1">No creditors found</Typography>
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={Math.ceil(creditors.length / creditorsPerPage)}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
          color="primary"
          size={isMobile ? "small" : "medium"}
          sx={{
            "& .MuiPaginationItem-root": {
              "&.Mui-selected": {
                bgcolor: PRIMARY_COLOR,
                "&:hover": {
                  bgcolor: SECONDARY_COLOR,
                },
              },
            },
          }}
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleAddNew();
            handleMenuClose();
          }}
          sx={{
            gap: 1.5,
            py: 1.5,
            px: 2,
            "&:hover": {
              backgroundColor: alpha(PRIMARY_COLOR, 0.05),
            },
          }}
        >
          <AddIcon fontSize="small" sx={{ color: PRIMARY_COLOR }} />
          <Typography variant="body2">Add New</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleEdit}
          sx={{
            gap: 1.5,
            py: 1.5,
            px: 2,
            "&:hover": {
              backgroundColor: alpha(PRIMARY_COLOR, 0.05),
            },
          }}
        >
          <EditIcon fontSize="small" sx={{ color: PRIMARY_COLOR }} />
          <Typography variant="body2">Edit</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{
            gap: 1.5,
            py: 1.5,
            px: 2,
            "&:hover": {
              backgroundColor: alpha(theme.palette.error.main, 0.05),
            },
          }}
        >
          <DeleteIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
          <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
            Delete
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default Allowances;